/**
 * GroupSyncDialog Component
 * Handles synchronization of groups from external providers (Entra, Google, etc.)
 */

import {
  Sync as SyncIcon,
  Cloud as CloudIcon,
  CheckCircle as CheckCircleIcon,
  ExpandLess,
  ExpandMore,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  LinearProgress,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Collapse,
  Divider,
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../utils/formatting/formatters';

import { apiClient } from '../../config/apiConfig';

interface SyncResult {
  organization_id: string;
  sync_timestamp: string;
  providers: Record<string, {
    provider: string;
    synced: number;
    created: number;
    updated: number;
    errors: string[];
  }>;
  total_synced: number;
  total_created: number;
  total_updated: number;
  errors: string[];
}

interface GroupSyncDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const GroupSyncDialog: React.FC<GroupSyncDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { i18n } = useTranslation();
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedProviders, setExpandedProviders] = useState<Set<string>>(new Set());

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setSyncResult(null);
      setError(null);
      setSyncing(false);
      setExpandedProviders(new Set());
    }
  }, [open]);

  const handleSync = async () => {
    try {
      setSyncing(true);
      setError(null);
      setSyncResult(null);

      const result = await apiClient.request<SyncResult>({
        path: '/api/groups/sync',
        method: 'POST',
        body: {
          provider: null, // Sync all providers
          force: false
        },
        format: 'json',
      });

      setSyncResult(result.data);
      onSuccess();

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const toggleProviderExpansion = (provider: string) => {
    setExpandedProviders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(provider)) {
        newSet.delete(provider);
      } else {
        newSet.add(provider);
      }
      return newSet;
    });
  };

  const getProviderIcon = (provider: string) => {
    switch (provider.toLowerCase()) {
      case 'entra':
        return <CloudIcon color="info" />;
      case 'google':
        return <CloudIcon color="success" />;
      case 'okta':
        return <CloudIcon color="warning" />;
      default:
        return <CloudIcon />;
    }
  };

  const handleClose = () => {
    if (!syncing) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SyncIcon />
          <Typography variant="h6">Sync Groups from External Providers</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {!syncResult && !error && (
          <Box>
            <Typography variant="body1" gutterBottom>
              Synchronize groups from configured external providers (Microsoft Entra, Google Workspace, etc.)
            </Typography>
            
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>What happens during sync:</strong>
              </Typography>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>Fetch all groups from external providers</li>
                <li>Create new groups that don't exist locally</li>
                <li>Update existing group information</li>
                <li>Sync group memberships for users</li>
                <li>Preserve local group permissions and settings</li>
              </ul>
            </Alert>

            {syncing && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" gutterBottom>
                  Synchronizing groups...
                </Typography>
                <LinearProgress />
              </Box>
            )}
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Sync Failed:</strong> {error}
            </Typography>
          </Alert>
        )}

        {syncResult && (
          <Box>
            {/* Summary */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="success.main">
                  <CheckCircleIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Sync Completed Successfully
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="primary">
                      {syncResult.total_synced}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total Synced
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main">
                      {syncResult.total_created}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Created
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="info.main">
                      {syncResult.total_updated}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Updated
                    </Typography>
                  </Box>
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                  Synced at: {formatDateTime(syncResult.sync_timestamp, i18n.language)}
                </Typography>
              </CardContent>
            </Card>

            {/* Provider Details */}
            <Typography variant="h6" gutterBottom>
              Provider Results
            </Typography>

            <List>
              {Object.entries(syncResult.providers).map(([providerName, result]) => (
                <Box key={providerName}>
                  <ListItem disablePadding>
                    <ListItemButton onClick={() => toggleProviderExpansion(providerName)}>
                      <ListItemIcon>
                        {getProviderIcon(providerName)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="subtitle1">
                              {providerName.toUpperCase()}
                            </Typography>
                            <Chip
                              size="small"
                              label={`${result.synced} synced`}
                              color="primary"
                              variant="outlined"
                            />
                            {result.created > 0 && (
                              <Chip
                                size="small"
                                label={`${result.created} new`}
                                color="success"
                                variant="outlined"
                              />
                            )}
                            {result.updated > 0 && (
                              <Chip
                                size="small"
                                label={`${result.updated} updated`}
                                color="info"
                                variant="outlined"
                              />
                            )}
                            {result.errors.length > 0 && (
                              <Chip
                                size="small"
                                label={`${result.errors.length} errors`}
                                color="error"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        }
                      />
                      <IconButton>
                        {expandedProviders.has(providerName) ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </ListItemButton>
                  </ListItem>

                  <Collapse in={expandedProviders.has(providerName)} timeout="auto" unmountOnExit>
                    <Box sx={{ pl: 4, pr: 2, pb: 2 }}>
                      {result.errors.length > 0 && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          <Typography variant="body2" gutterBottom>
                            <strong>Sync Errors:</strong>
                          </Typography>
                          {result.errors.map((error, index) => (
                            <Typography key={index} variant="body2">
                              • {error}
                            </Typography>
                          ))}
                        </Alert>
                      )}

                      <Typography variant="body2" color="text.secondary">
                        Successfully synced {result.synced} groups from {providerName}
                        {result.created > 0 && ` (${result.created} new groups created)`}
                        {result.updated > 0 && ` (${result.updated} groups updated)`}
                      </Typography>
                    </Box>
                  </Collapse>

                  <Divider />
                </Box>
              ))}
            </List>

            {/* Global Errors */}
            {syncResult.errors.length > 0 && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>General Sync Errors:</strong>
                </Typography>
                {syncResult.errors.map((error, index) => (
                  <Typography key={index} variant="body2">
                    • {error}
                  </Typography>
                ))}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {!syncResult && (
          <>
            <Button onClick={handleClose} disabled={syncing}>
              Cancel
            </Button>
            <Button
              onClick={handleSync}
              variant="contained"
              startIcon={syncing ? <RefreshIcon /> : <SyncIcon />}
              disabled={syncing}
            >
              {syncing ? 'Syncing...' : 'Start Sync'}
            </Button>
          </>
        )}
        
        {syncResult && (
          <>
            <Button onClick={handleSync} startIcon={<RefreshIcon />}>
              Sync Again
            </Button>
            <Button onClick={handleClose} variant="contained">
              Close
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
